import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendUp, TrendDown, Waveform, PushPin } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const RANGE_KEY = "stratos.moodFeed.range";
const RANGES = [
  { key: "24h", days: 1 },
  { key: "7d",  days: 7 },
];

/**
 * Horizontal strip of the market's biggest bullish % swings over the past
 * ``days``. Renders on the dashboard so traders see market-wide mood
 * movement at a glance, without opening every symbol drawer.
 *
 * Hidden when the backend has no snapshots yet (avoids an empty card
 * cluttering the dashboard for fresh accounts).
 */
export default function MoodFeedStrip({ limit = 6 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [watched, setWatched] = useState(new Set()); // symbols already on user's watchlist
  const [pinning, setPinning] = useState(null); // symbol currently being pinned
  const [rangeKey, setRangeKey] = useState(() => {
    try { return localStorage.getItem(RANGE_KEY) || "24h"; } catch { return "24h"; }
  });
  const range = RANGES.find((r) => r.key === rangeKey) || RANGES[0];
  const days = range.days;

  // Pull the user's watchlist once on mount so we can hide the pin icon on
  // chips they've already saved. Refreshed silently after each pin.
  useEffect(() => {
    api.get("/watchlist")
      .then((r) => setWatched(new Set((r.data.items || []).map((it) => it.symbol))))
      .catch(() => {});
  }, []);

  const pinSymbol = async (symbol) => {
    if (watched.has(symbol)) return;
    setPinning(symbol);
    try {
      await api.post(`/watchlist`, { symbol });
      setWatched((s) => new Set(s).add(symbol));
      toast.success(`${symbol} added to your watchlist`);
    } catch (e) {
      toast.error(`Couldn't pin ${symbol}`);
    } finally {
      setPinning(null);
    }
  };

  useEffect(() => {
    let stop = false;
    setLoading(true);
    const load = () => api.get(`/market/mood-feed?days=${days}&limit=${limit}`)
      .then((r) => { if (!stop) setItems(r.data.items || []); })
      .catch(() => { if (!stop) setItems([]); })
      .finally(() => { if (!stop) setLoading(false); });
    load();
    // Refresh every 3 min — sentiment snapshots ingest roughly hourly, so a
    // faster cadence would just spend requests for no new data.
    const t = setInterval(load, 180_000);
    return () => { stop = true; clearInterval(t); };
  }, [days, limit]);

  const changeRange = (v) => {
    setRangeKey(v);
    try { localStorage.setItem(RANGE_KEY, v); } catch {}
  };

  if (loading || items.length === 0) return null;

  return (
    <div
      data-testid="mood-feed-strip"
      className="border border-zinc-800 rounded-sm bg-zinc-900/40 px-3 py-2.5"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest flex items-center gap-1.5">
          <Waveform size={12} /> Market mood shifts · last {rangeKey === "24h" ? "24h" : "7d"}
        </div>
        <div className="flex items-center gap-2">
          <div
            data-testid="mood-feed-range-toggle"
            role="tablist"
            aria-label="Mood feed range"
            className="flex items-center border border-zinc-800 rounded-sm overflow-hidden"
          >
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                role="tab"
                aria-selected={rangeKey === r.key}
                onClick={() => changeRange(r.key)}
                data-testid={`mood-feed-range-${r.key}`}
                className={`mono text-[9px] uppercase tracking-widest px-2 py-1 transition-colors ${
                  rangeKey === r.key
                    ? "bg-zinc-100 text-black"
                    : "text-zinc-500 hover:text-white hover:bg-zinc-800"
                }`}
              >
                {r.key}
              </button>
            ))}
          </div>
          <div className="mono text-[9px] text-zinc-600 uppercase tracking-widest">
            {items.length} movers
          </div>
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin">
        {items.map((it) => {
          const up = it.delta_pct > 0;
          const color = up ? "#00C805" : "#FF3B30";
          const isWatched = watched.has(it.symbol);
          const isPinning = pinning === it.symbol;
          return (
            <div
              key={it.symbol}
              data-testid={`mood-feed-item-${it.symbol}`}
              className="shrink-0 min-w-[92px] relative px-2.5 py-2 border rounded-sm bg-black/40 hover:bg-black/70 transition-colors"
              style={{ borderColor: `${color}66` }}
            >
              {/* Pin corner button — hidden once the symbol is on the
                  watchlist so it doesn't feel duplicative. */}
              {!isWatched && (
                <button
                  type="button"
                  data-testid={`mood-feed-pin-${it.symbol}`}
                  disabled={isPinning}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    pinSymbol(it.symbol);
                  }}
                  title={`Pin ${it.symbol} to your watchlist`}
                  className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-zinc-950 border border-zinc-700 text-zinc-400 hover:text-white hover:border-white flex items-center justify-center disabled:opacity-50 disabled:cursor-wait transition-colors"
                >
                  <PushPin size={8} weight="regular" />
                </button>
              )}
              {isWatched && (
                <span
                  data-testid={`mood-feed-pinned-${it.symbol}`}
                  title={`${it.symbol} is on your watchlist`}
                  className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#00C805]/20 border border-[#00C805]/60 text-[#00C805] flex items-center justify-center"
                >
                  <PushPin size={8} weight="fill" />
                </span>
              )}
              <Link
                to={`/positions?s=${it.symbol}&drawer=news`}
                title={`${it.symbol} · ${Math.round(it.start_pct)}% → ${Math.round(it.end_pct)}% bull · click for headlines`}
                className="block"
              >
                <div className="flex items-baseline justify-between gap-1">
                  <span className="mono text-xs font-bold text-white">{it.symbol}</span>
                  <span
                    data-testid={`mood-feed-delta-${it.symbol}`}
                    className="mono text-[10px] font-bold leading-none flex items-center gap-0.5"
                    style={{ color }}
                  >
                    {up ? <TrendUp size={9} weight="bold" /> : <TrendDown size={9} weight="bold" />}
                    {Math.abs(Math.round(it.delta_pct))}%
                  </span>
                </div>
                <div className="mono text-[9px] text-zinc-500 mt-1">
                  {Math.round(it.start_pct)}% → {Math.round(it.end_pct)}% bull
                </div>
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}
